'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { QuizQuestion, UserAnswers } from '@/types/dogs';
import { submitQuizAnswers } from '@/lib/api/client';

export function QuizForm({ questions }: { questions: QuizQuestion[] }) {
  const router = useRouter();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<UserAnswers>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAnswer = async (value: string | number | boolean) => {
    const newAnswers = {
      ...answers,
      [questions[currentQuestion].id]: value
    };
    setAnswers(newAnswers);

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      setIsSubmitting(true);
      try {
        const matches = await submitQuizAnswers(newAnswers);
        localStorage.setItem('quizResults', JSON.stringify(matches));
        router.push('/quiz/results');
      } catch (error) {
        console.error('Failed to submit quiz:', error);
        alert('Failed to process quiz results. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const currentQ = questions[currentQuestion];

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="mb-6">
        <div className="flex justify-between text-sm text-gray-500 mb-2">
          <span>Question {currentQuestion + 1} of {questions.length}</span>
          <span>{Math.round((currentQuestion / questions.length) * 100)}% complete</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-primary rounded-full h-2 transition-all duration-300"
            style={{ width: `${(currentQuestion / questions.length) * 100}%` }}
          />
        </div>
      </div>

      <h2 className="text-xl font-semibold mb-2">{currentQ.question}</h2>
      <p className="text-gray-600 mb-6">{currentQ.description}</p>

      <div className="space-y-3">
        {currentQ.options.map((option, index) => (
          <button
            key={index}
            onClick={() => handleAnswer(option.value)}
            className={`w-full p-4 text-left rounded-lg border transition-all duration-200
              ${answers[currentQ.id] === option.value
                ? 'border-primary bg-primary/5'
                : 'border-gray-200 hover:border-primary/50 hover:bg-gray-50'
              }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      {isSubmitting && (
        <div className="mt-6 text-center text-gray-600">
          Finding your perfect matches...
        </div>
      )}
    </div>
  );
} 